import { fetchPlantao } from './api';
import { dict } from './dict';

/**
 * Aviso de mensagem nova: som e notificação do navegador.
 *
 * ── Por que fica desligado por padrão ──
 * Som que toca sem alguém ter pedido é a primeira coisa que se desliga num
 * produto — e depois de desligado no sistema operacional, não volta. A
 * preferência mora no navegador porque é jeito de trabalhar, não dado: a mesma
 * atendente pode querer som no computador do consultório e silêncio em casa.
 */
const CHAVE = 'zenda.avisos';

export interface Preferencia {
  readonly som: boolean;
  readonly notificacao: boolean;
  /** Fora do turno, só avisa conversa de médico da própria operação (equipes.md §11). */
  readonly soCasaForaDoTurno: boolean;
}

const DESLIGADO: Preferencia = { som: false, notificacao: false, soCasaForaDoTurno: false };

export function lerPreferencia(): Preferencia {
  try {
    const bruto = window.localStorage.getItem(CHAVE);
    if (bruto === null) return DESLIGADO;
    const p = JSON.parse(bruto) as Partial<Preferencia>;
    return {
      som: p.som === true,
      notificacao: p.notificacao === true,
      soCasaForaDoTurno: p.soCasaForaDoTurno === true,
    };
  } catch {
    return DESLIGADO;
  }
}

export function salvarPreferencia(p: Preferencia): void {
  window.localStorage.setItem(CHAVE, JSON.stringify(p));
}

export function podeNotificar(): boolean {
  return typeof Notification !== 'undefined';
}

/** Só o navegador concede, e só a partir de um gesto — daí ser chamado no clique. */
export async function pedirPermissao(): Promise<boolean> {
  if (!podeNotificar()) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  return (await Notification.requestPermission()) === 'granted';
}

/**
 * O som, gerado na hora.
 *
 * ── Por que não um arquivo ──
 * Um mp3 seria mais um asset para carregar, versionar e servir — e o que se
 * quer aqui são duas notas curtas. O oscilador do WebAudio faz isso em vinte
 * linhas, sem rede, e soa igual em qualquer navegador.
 *
 * O `AudioContext` é criado UMA vez e reaproveitado: cada instância custa um
 * thread de áudio no sistema, e criar um por mensagem esgotaria o limite do
 * navegador num dia de movimento.
 */
let audio: AudioContext | null = null;

export function tocar(): void {
  try {
    audio ??= new AudioContext();
    // Navegador suspende o contexto até haver interação; sem isto o primeiro
    // som de cada sessão seria silêncio.
    if (audio.state === 'suspended') void audio.resume();

    const agora = audio.currentTime;
    // Duas notas subindo — "chegou algo", não "deu erro". A segunda entra com o
    // fim da primeira, e o conjunto dura menos de um terço de segundo: aviso que
    // se ouve sem interromper quem está digitando.
    for (const [i, hz] of [880, 1175].entries()) {
      const osc = audio.createOscillator();
      const ganho = audio.createGain();
      osc.type = 'sine';
      osc.frequency.value = hz;

      const inicio = agora + i * 0.09;
      // Rampa em vez de liga/desliga: corte seco em onda senoidal produz um
      // "clique" audível, que é o que faz um aviso soar barato.
      ganho.gain.setValueAtTime(0, inicio);
      ganho.gain.linearRampToValueAtTime(0.12, inicio + 0.01);
      ganho.gain.exponentialRampToValueAtTime(0.0001, inicio + 0.16);

      osc.connect(ganho).connect(audio.destination);
      osc.start(inicio);
      osc.stop(inicio + 0.18);
    }
  } catch {
    // Sem áudio disponível o produto continua funcionando. Não é erro.
  }
}

/**
 * Chamado quando chega mensagem do paciente.
 *
 * A notificação só aparece com a aba ESCONDIDA. Com a tela à vista, a conversa
 * já pulou para o topo e o badge já mudou — uma notificação do sistema por cima
 * disso é barulho, e barulho é o que faz a pessoa desligar o aviso inteiro.
 */
let plantao: { temTurno: boolean; dePlantao: boolean; lidoEm: number } | null = null;
const PLANTAO_VALE_MS = 5 * 60_000;

/** O turno muda de hora em hora, e não a cada mensagem: lido no máximo a cada 5 min. */
async function foraDoTurno(): Promise<boolean> {
  if (plantao === null || Date.now() - plantao.lidoEm > PLANTAO_VALE_MS) {
    try {
      const r = await fetchPlantao();
      plantao = { ...r, lidoEm: Date.now() };
    } catch {
      return false;
    }
  }
  return plantao.temTurno && !plantao.dePlantao;
}

export async function avisar(daCasa = true): Promise<void> {
  const p = lerPreferencia();
  if (!daCasa && p.soCasaForaDoTurno && (await foraDoTurno())) return;
  if (p.som) tocar();

  if (!p.notificacao || !podeNotificar()) return;
  if (Notification.permission !== 'granted') return;
  if (document.visibilityState === 'visible') return;

  try {
    // Sem corpo de mensagem: notificação do sistema aparece na tela de bloqueio
    // e em tela compartilhada, e dado de paciente não vai para lá.
    // `tag` fixa: cinco mensagens em sequência substituem a mesma notificação
    // em vez de empilharem cinco. Sem isto, sair para o almoço e voltar rende
    // uma torre de avisos que a pessoa fecha um a um — e nunca mais liga.
    // Sem `icon`: o projeto não tem favicon, e apontar para um arquivo que não
    // existe rende 404 no console de todo mundo.
    const n = new Notification(dict().aviso.titulo, {
      body: dict().aviso.corpo,
      tag: 'zenda-mensagem',
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // Idem: aviso é acessório.
  }
}
